/**
 * Spearman's rank correlation.
 *
 * ρ is Pearson's r computed on the ranks, with tied values given their average rank —
 * the definition SciPy's `spearmanr` uses, and the only one that stays correct when
 * there are ties. The textbook shortcut 1 − 6Σd² / (n(n² − 1)) equals it exactly only
 * when there are no ties, so it is reported alongside for the working, not instead.
 *
 * Significance uses the t approximation t = ρ√((n − 2) / (1 − ρ²)) with n − 2 degrees
 * of freedom (again SciPy's method). It is rough for very small n, where exact tables of
 * ρ's permutation distribution are the better reference.
 */

import { averageRanks, pearson, tieGroupSizes } from "@/lib/stats/descriptive";
import { studentTTwoTailed } from "@/lib/stats/distributions";

export interface RankRow {
  x: number;
  y: number;
  rankX: number;
  rankY: number;
  /** rankX − rankY. */
  d: number;
  d2: number;
}

export interface SpearmanReport {
  n: number;
  rows: RankRow[];
  rho: number;
  sumD2: number;
  /** 1 − 6Σd² / (n(n² − 1)): exact only without ties. */
  shortcutRho: number;
  hasTies: boolean;
  t: number;
  df: number;
  /** Two-sided p-value from the t approximation. */
  p: number;
  /** Pearson's r on the raw values, for comparison. */
  pearsonR: number;
}

/** t for a correlation coefficient with n − 2 df; ±∞ for a perfect correlation. */
export function correlationT(r: number, n: number): number {
  if (Math.abs(r) >= 1) {
    return r > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }
  return r * Math.sqrt((n - 2) / (1 - r * r));
}

export function spearmanReport(
  xs: readonly number[],
  ys: readonly number[]
): SpearmanReport | null {
  const n = xs.length;
  if (n < 3 || ys.length !== n) {
    return null;
  }
  const ranksX = averageRanks(xs);
  const ranksY = averageRanks(ys);
  const rho = pearson(ranksX, ranksY);
  if (Number.isNaN(rho)) {
    return null;
  }
  const rows: RankRow[] = [];
  let sumD2 = 0;
  for (let i = 0; i < n; i++) {
    const d = ranksX[i] - ranksY[i];
    rows.push({
      x: xs[i],
      y: ys[i],
      rankX: ranksX[i],
      rankY: ranksY[i],
      d,
      d2: d * d,
    });
    sumD2 += d * d;
  }
  const t = correlationT(rho, n);
  const df = n - 2;
  return {
    n,
    rows,
    rho,
    sumD2,
    shortcutRho: 1 - (6 * sumD2) / (n * (n * n - 1)),
    hasTies: tieGroupSizes(xs).length > 0 || tieGroupSizes(ys).length > 0,
    t,
    df,
    p: Number.isFinite(t) ? studentTTwoTailed(t, df) : 0,
    pearsonR: pearson(xs, ys),
  };
}
